import useInterval from '@/hooks/useInterval'
import { PaginationProps } from '@/shared/Pagination'
import { showRebootModal } from '@/shared/RebootModal'
import { TemplateItem, VariableItem } from '@/template/interface'
import { logger } from '@/utils/logger'
import { delay, getFormatMessage } from '@/utils/utils'
import { FrownOutlined, SyncOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  message,
  PageHeader,
  Result,
  Space,
  Spin,
  Tabs
} from 'antd'
import React, { Fragment, useEffect, useState } from 'react'
import {
  BoxApnItem,
  BoxAuditItem,
  BoxConnectionItem,
  BoxDetailItem,
  BoxDetailProps,
  BoxItem,
  BoxLogItem,
  BoxNetworkItem,
  BoxNetworkModeItem,
  BoxPushStatus,
  BoxTimeItem,
  BoxWifiItem
} from '../interface'
import { BoxDetailAudit } from './audit'
import { BoxDetailBasic } from './basic'
import { BoxDetailLog } from './logs'
import { BoxDetailStatus } from './status'
import { BoxDetailTemplate } from './template'

export const BoxDetail: React.FC<BoxDetailProps> = (props: BoxDetailProps) => {
  const formatMessage = getFormatMessage()
  const {
    boxItem,
    fixedStyle,
    hideTabs,
    values,
    valueMode,
    //
    onBoxDetailLoad,
    onBoxTemplateLoad,
    onBoxAuditListLoad,
    onBoxLogListLoad,
    onBoxValueRead,
    onBoxValueWrite,
    //
    onBoxNetworkModeUpdate,
    onBoxNetworkUpdate,
    onBoxApnUpdate,
    onBoxConnectionUpdate,
    onBoxTimeUpdate,
    onBoxVariablePushChange,
    onBoxAlarmPushChange,
    onBoxReboot,
    onBoxValueSubscribe,
    onBoxWifiLoad
  } = props
  const [boxTemplate, setBoxTemplate] = useState<TemplateItem>()
  const [boxAuditItems, setBoxAuditItems] = useState<BoxAuditItem[]>([])
  const [boxAuditTotal, setBoxAuditTotal] = useState<number>(0)
  const [boxAuditSearch, setBoxAuditSearch] = useState<any>()
  const [boxLogItems, setBoxLogItems] = useState<BoxLogItem[]>([])
  const [boxLogTotal, setBoxLogTotal] = useState<number>(0)
  const [boxDetailItem, setBoxDetailItem] = useState<BoxDetailItem>()
  const [tabKey, setTabKey] = useState<string>('detail.basic')
  const [templateTabKey, setTemplateTabKey] = useState<string>()
  // const [variableSkip, setVariableSkip] = useState<number>(0)
  const [variablePageNumber, setVariablePageNumber] = useState<number>(1)
  const [variablePageSize, setVariablePageSize] = useState<number>(10)
  const [variableSearch, setVariableSearch] = useState<any>({})
  const [boxWifiItems, setBoxWifiItems] = useState<BoxWifiItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  useInterval(
    () => {
      if (boxTemplate) {
        loadValues(variablePageNumber, variablePageSize, boxTemplate) // 按页加载
      }
    },
    valueMode === 'pull' && // 拉取模式
      boxItem?.connectionState === 1 && // 盒子在线
      boxDetailItem?.enableItemPush && // 启用推送
      templateTabKey === 'item.variable' // 当前选项卡为监控点
      ? 2000
      : null
  )

  const auditPagination: PaginationProps = {
    total: boxAuditTotal,
    onChange: async (pageNumber: number, pageSize?: number) => {
      if (!boxItem) return
      const skip = (pageNumber - 1) * (pageSize || 10)
      await loadBoxAudits(boxItem, skip, boxAuditSearch)
    }
  }

  const logPagination: PaginationProps = {
    total: boxLogTotal,
    onChange: async (pageNumber: number, pageSize?: number) => {
      if (!boxItem) return
      const skip = (pageNumber - 1) * (pageSize || 10)
      await loadBoxLogs(boxItem, skip, boxAuditSearch)
    }
  }

  const variablePagination: PaginationProps = {
    showSizeChanger: false,
    current: variablePageNumber,
    pageSize: variablePageSize,
    onChange: async (pageNumber: number, pageSize?: number) => {
      if (boxTemplate) {
        setVariablePageNumber(pageNumber)
        setVariablePageSize(pageSize || 10)
        await loadValues(pageNumber, pageSize || 10, boxTemplate)
      }
    }
  }

  const emptyResult = (
    <Result icon={<FrownOutlined />} title={'设备列表为空，请先添加设备。'} />
  )

  const errorResult = (
    <Result
      status='error'
      title={formatMessage({ id: '获取设备信息失败。' })}
      extra={
        <Button
          type='primary'
          onClick={async () => {
            await loadBoxDetail(boxItem!, true)
          }}
        >
          {formatMessage({ id: '重新获取' })}
        </Button>
      }
    />
  )

  const actionKeys = ['detail.basic', 'detail.status', 'detail.template']

  const tabActions = actionKeys.includes(tabKey) ? (
    <Space>
      <Button
        type='link'
        disabled={!boxItem}
        onClick={async (e) => {
          e.preventDefault()
          if (tabKey === 'detail.template') {
            await loadBoxTemplate(boxItem!, true)
          } else {
            await loadBoxDetail(boxItem!, true)
          }
        }}
      >
        <SyncOutlined />
      </Button>
    </Space>
  ) : null

  let tabList = [
    {
      key: 'detail.basic',
      tab: formatMessage({ id: 'app.box.detail.basic.title' }),
      panel: () =>
        !boxItem ? (
          emptyResult
        ) : !boxDetailItem ? (
          errorResult
        ) : (
          <BoxDetailBasic
            detailItem={boxDetailItem}
            wifiItems={boxWifiItems}
            onNetworkModeUpdate={handleBoxNetworkModeUpdate}
            onNetworkUpdate={handleBoxNetworkUpdate}
            onApnUpdate={handleBoxApnUpdate}
            onConnectionUpdate={handleBoxConnectionUpdate}
            onTimeUpdate={handleBoxTimeUpdate}
            onWifiScan={handleBoxWifiLoad}
          />
        )
    },
    {
      key: 'detail.status',
      tab: formatMessage({ id: 'app.box.detail.status.title' }),
      panel: () =>
        !boxItem ? (
          emptyResult
        ) : !boxDetailItem ? (
          errorResult
        ) : (
          <BoxDetailStatus detailItem={boxDetailItem} />
        )
    },
    {
      key: 'detail.template',
      tab: formatMessage({ id: 'app.box.detail.template.title' }),
      panel: () =>
        !boxItem ? (
          emptyResult
        ) : !boxDetailItem ? (
          errorResult
        ) : (
          <BoxDetailTemplate
            detailItem={boxDetailItem}
            template={boxTemplate}
            variableValues={values}
            variablePagination={variablePagination}
            onValueSet={handleValueSet}
            onVariablePushStatusChange={handleVariablePushStatusChange}
            onVariableSearch={handleVariableSearch}
            onAlarmPushStatusChange={handleAlarmPushStatusChange}
            onDestory={handleTemplateDestory}
            onTabChange={handleTemplateTabChange}
          />
        )
    },
    {
      key: 'detail.audit',
      tab: formatMessage({ id: 'app.box.detail.audit.title' }),
      panel: () =>
        !boxItem ? (
          emptyResult
        ) : (
          <BoxDetailAudit
            boxNo={boxDetailItem?.boxNo}
            data={boxAuditItems}
            pagination={auditPagination}
            onSearch={handleBoxAuditSearch}
          />
        )
    },
    {
      key: 'detail.logs',
      tab: formatMessage({ id: 'app.box.detail.logs.title' }),
      panel: () =>
        !boxItem ? (
          emptyResult
        ) : (
          <BoxDetailLog data={boxLogItems} pagination={logPagination} />
        )
    }
  ]

  if (hideTabs && hideTabs.length > 0) {
    tabList = tabList.filter((x) => !hideTabs.includes(x.key))
  }

  // 加载盒子详情
  const loadBoxDetail = async (item: BoxItem, fromBox: boolean) => {
    try {
      setLoading(true)
      const result = await onBoxDetailLoad?.(item, fromBox)
      setBoxDetailItem(result)
    } catch (error) {
      setBoxDetailItem(undefined)
    } finally {
      setLoading(false)
    }
  }

  // 加载盒子模板
  const loadBoxTemplate = async (item: BoxItem, fromBox: boolean) => {
    try {
      setLoading(true)
      const templateResult = await onBoxTemplateLoad?.(item, fromBox)
      if (templateResult) {
        setBoxTemplate(templateResult)
        // 重置监控点检索分页参数
        setVariablePageNumber(1)
        setVariablePageSize(10)
        setVariableSearch({})
        await onBoxValueSubscribe?.(item, true)
        if (!boxDetailItem?.enableItemPush) {
          await loadValues(1, 10, templateResult, {})
        }
      }
    } catch (error) {
      setBoxTemplate(undefined)
    } finally {
      setLoading(false)
    }
  }

  // 加载盒子审计日志
  const loadBoxAudits = async (item: BoxItem, skip: number = 0, args?: any) => {
    const auditResult = await onBoxAuditListLoad?.(item, skip, args)
    setBoxAuditItems(auditResult?.items || [])
    setBoxAuditTotal(auditResult?.totalCount || 0)
  }

  // 加载盒子上下线日志
  const loadBoxLogs = async (item: BoxItem, skip: number = 0, args?: any) => {
    const logResult = await onBoxLogListLoad?.(item, skip, args)
    setBoxLogItems(logResult?.items || [])
    setBoxLogTotal(logResult?.totalCount || 0)
  }

  // 加载监控点数据(分页)
  const loadValues = async (
    pageNumber: number,
    pageSize: number,
    template: TemplateItem,
    searchArgs?: any
  ) => {
    if (!boxItem) return
    if (boxItem.connectionState !== 1) return
    if (!template.variables) return
    let newValues = template.variables || []
    let args = searchArgs ? searchArgs : variableSearch
    if (args.status) {
      newValues = newValues.filter((x) => x['status'] === args.status)
    }
    if (args.deviceId) {
      newValues = newValues.filter((x) => x.deviceId === args.deviceId)
    }
    if (args.name) {
      newValues = newValues.filter(
        (x) => x.name?.includes(args.name) || x.description?.includes(args.name)
      )
    }
    const skip = (pageNumber - 1) * pageSize
    const names = newValues.slice(skip, skip + pageSize).map((x) => x.name!)
    if (names.length > 0) {
      await onBoxValueRead?.(boxItem, names)
    }
  }

  // 初始加载/设备切换时加载
  const initLoad = async (boxItem: BoxItem, force: boolean = false) => {
    switch (tabKey) {
      case 'detail.basic': {
        await loadBoxDetail(boxItem, force)
        break
      }
      case 'detail.status': {
        await loadBoxDetail(boxItem, force)
        break
      }
      case 'detail.template': {
        // template 依赖DetailItem
        await loadBoxDetail(boxItem, force)
        await loadBoxTemplate(boxItem, force)
        break
      }
      case 'detail.audit': {
        await loadBoxDetail(boxItem, force)
        await loadBoxAudits(boxItem)
        break
      }
      case 'detail.logs': {
        await loadBoxDetail(boxItem, force)
        await loadBoxLogs(boxItem)
        break
      }
    }
  }

  const handleBoxWifiLoad = async () => {
    logger.info(`BoxWifiLoad:`)
    if (!boxItem) return
    const items = (await onBoxWifiLoad?.(boxItem)) || []
    console.log(items)
    setBoxWifiItems(items)
  }

  // 更新网络模式
  const handleBoxNetworkModeUpdate = async (
    networkMode: BoxNetworkModeItem
  ) => {
    logger.info(`BoxNetworkModeUpdate: ${JSON.stringify(networkMode)}`)
    if (!boxItem) return
    if (onBoxNetworkModeUpdate) {
      try {
        logger.info('BoxNetworkModeUpdate emit.')
        await onBoxNetworkModeUpdate([boxItem], networkMode)
        showRebootModal(async () => {
          await onBoxReboot?.([boxItem])
        })
      } catch (error) {
        logger.error('BoxNetworkModeUpdate error')
      }
    }
  }

  // 更新网络参数
  const handleBoxNetworkUpdate = async (network: BoxNetworkItem) => {
    if (!boxItem) return
    if (onBoxNetworkUpdate) {
      try {
        await onBoxNetworkUpdate([boxItem], network)
        showRebootModal(async () => {
          await onBoxReboot?.([boxItem])
        })
      } catch (error) {}
    }
  }

  // 更新APN设置
  const handleBoxApnUpdate = async (apn: BoxApnItem) => {
    if (!boxItem) return
    if (onBoxApnUpdate) {
      try {
        await onBoxApnUpdate([boxItem], apn)
        showRebootModal(async () => {
          await onBoxReboot?.([boxItem])
        })
      } catch (error) {}
    }
  }

  // 更新连接设置
  const handleBoxConnectionUpdate = async (connection: BoxConnectionItem) => {
    if (!boxItem) return
    if (onBoxConnectionUpdate) {
      await onBoxConnectionUpdate([boxItem], connection)
      showRebootModal(async () => {
        await onBoxReboot?.([boxItem])
      })
    }
  }

  // 更新时钟设置
  const handleBoxTimeUpdate = async (time: BoxTimeItem) => {
    if (!boxItem) return
    if (onBoxTimeUpdate) {
      await onBoxTimeUpdate([boxItem], time)
      message.success(formatMessage({ id: '时钟配置设置成功' }))
    }
  }

  // 搜索审计日志
  const handleBoxAuditSearch = async (args: { type?: number }) => {
    if (!boxItem) return
    setBoxAuditSearch(args)
    await loadBoxAudits(boxItem, 0, args)
  }

  // 模板组件销毁
  const handleTemplateDestory = () => {
    setTemplateTabKey(undefined)
    setVariablePageNumber(1)
    setVariablePageSize(10)
    if (!boxItem) return
    onBoxValueSubscribe?.(boxItem, false)
  }

  // 模板组件选项卡切换
  const handleTemplateTabChange = (key: string) => {
    setTemplateTabKey(key)
  }

  // 切换详情选项卡
  const handleDetailTabChange = (key: string) => {
    setTabKey(key)
    if (!boxItem) return
    switch (key) {
      case 'detail.template':
        if (boxDetailItem) {
          loadBoxTemplate(boxItem, false)
        }
        break
      case 'detail.audit':
        loadBoxAudits(boxItem)
        break
      case 'detail.logs':
        loadBoxLogs(boxItem)
        break
    }
  }

  // 写值
  const handleValueSet = async (variable?: VariableItem, value?: any) => {
    if (!boxItem) return
    const name = variable?.name!
    if (onBoxValueWrite) {
      await onBoxValueWrite(boxItem, [{ name: name, value: value }])
      // 拉取模式写完值后需要主动获取一下
      if (valueMode === 'pull' && !boxDetailItem?.enableItemPush) {
        // 延迟一秒后读取
        await delay(1000)
        // 写值后立即读取一次数据(todo: 验证是否兼容SE)
        await onBoxValueRead?.(boxItem, [name])
      }
    }
  }

  // 开启监控点推送
  const handleVariablePushStatusChange = async (status: BoxPushStatus) => {
    if (!boxItem) return
    if (!onBoxVariablePushChange) return
    // 发送开点请求(配置工具模式下无效，仅用作记录)
    try {
      await onBoxVariablePushChange([boxItem], status)
      // 成功后开启推送状态
      const newItem: BoxDetailItem = { ...boxDetailItem }
      newItem.enableItemPush = status
      setBoxDetailItem(newItem)
    } catch (error) {
      // not changed
    }
  }

  // 监控点过滤
  const handleVariableSearch = (args: any) => {
    setVariableSearch(args)
    // 如果未开启推送，过滤后需要主动获取一次状态
    if (!boxDetailItem?.enableItemPush && boxTemplate) {
      loadValues(variablePageNumber, variablePageSize, boxTemplate, args)
    }
  }

  // 开启报警推送
  const handleAlarmPushStatusChange = async (status: BoxPushStatus) => {
    if (!boxItem) return
    if (!onBoxAlarmPushChange) return
    try {
      await onBoxAlarmPushChange([boxItem], status)
      const newItem: BoxDetailItem = { ...boxDetailItem }
      newItem.enableAlarmPush = status
      setBoxDetailItem(newItem)
    } catch (error) {
      // not change
    }
  }

  // 切换盒子时(从缓存中初始化详情)
  useEffect(() => {
    if (boxItem) {
      // 强制刷新
      if (boxItem.connectionState == 4) {
        initLoad(boxItem, true)
      }
      // 详情不存在或者切换设备时从缓存获取
      else if (!boxDetailItem || boxItem.boxNo !== boxDetailItem.boxNo) {
        initLoad(boxItem)
      }
      // 连接状态发生变更
      else if (boxItem.connectionState !== boxDetailItem.connectionState) {
        if (boxItem.connectionState === 3) {
          // 设备离线，仅需要变更状态
          const newItem = { ...boxDetailItem }
          newItem.connectionState = boxItem.connectionState
          setBoxDetailItem(newItem)
        } else if (boxItem.connectionState === 1) {
          // 设备上线后，重新获取缓存
          initLoad(boxItem)
        }
      } else {
        // 如果状态未发变化，但是需要同步缓存
        initLoad(boxItem, false)
      }
    }
  }, [boxItem])

  return (
    <Fragment>
      {/* {boxItem && boxItem.connectionState !== 1 && (
        <div className={styles['offline-mask']}>
          <div>
            <SyncOutlined
              className={styles['offline-icon']}
              // onClick={onBoxSync}
            />
          </div>
          <div className={styles['offline-title']}>设备不在线</div>
        </div>
      )} */}

      <Spin spinning={loading}>
        <PageHeader
          style={{
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: '#fff'
          }}
          footer={
            <Tabs
              onChange={handleDetailTabChange}
              className='ant-pro-page-container-tabs'
              tabBarExtraContent={tabActions}
            >
              {tabList.map((item, index) => (
                <Tabs.TabPane
                  {...item}
                  tab={item.tab}
                  key={item.key || index}
                />
              ))}
            </Tabs>
          }
        />
        <Card bordered={false} style={fixedStyle}>
          {tabList.find((x) => x.key === tabKey)?.panel()}
        </Card>
      </Spin>
    </Fragment>
  )
}
